import { useEffect, useRef, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormControl,
    FormLabel,
    FormHelperText,
    FormErrorMessage,
    Alert,
    AlertIcon,
    AlertDescription,
    Box,
    Text,
    UnorderedList,
    ListItem,
} from "@chakra-ui/react";
import {
    PRICE_LIST_ACCEPT,
    PRICE_LIST_MAX_FILE_SIZE,
    formatDateOnly,
    isPublished,
    toDateOnly,
    zagrebToday,
} from "../../constants/priceLists";

const validateFile = (file) => {
    if (!file) return null;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xml")) return "Dopuštene su samo .csv i .xml datoteke.";
    if (file.size === 0) return "Datoteka je prazna.";
    if (file.size > PRICE_LIST_MAX_FILE_SIZE) return "Datoteka je veća od 5 MB.";
    return null;
};

// Handles three cases:
// - no priceList: a new upload (file required)
// - published priceList (date today or earlier): the backend never overwrites it,
//   it publishes a new version - so a file is required
// - scheduled priceList (future date): edited in place, file and date both optional
const UploadModal = ({ isOpen, onClose, onSave, priceList }) => {
    const today = zagrebToday();
    const [file, setFile] = useState(null);
    const [validForDate, setValidForDate] = useState(today);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef(null);

    const isEdit = !!priceList;
    const isNewVersion = isEdit && isPublished(priceList, today);
    const isScheduledEdit = isEdit && !isNewVersion;

    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setError(null);
            setValidForDate(isScheduledEdit ? toDateOnly(priceList.validForDate) : today);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, priceList]);

    const fileError = validateFile(file);
    const isDateChanged = isScheduledEdit && validForDate !== toDateOnly(priceList.validForDate);
    const canSave = isScheduledEdit
        ? !fileError && (!!file || isDateChanged) && !!validForDate
        : !!file && !fileError && !!validForDate;

    const title = isNewVersion ? "Objavi novu verziju" : isScheduledEdit ? "Uredi zakazani cjenik" : "Učitaj cjenik";

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            await onSave({ file, validForDate: isScheduledEdit && !isDateChanged ? null : validForDate });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent mx={4}>
                <ModalHeader>{title}</ModalHeader>
                <ModalCloseButton isDisabled={isSaving} />
                <ModalBody>
                    {isNewVersion && (
                        <Alert status="info" borderRadius="lg" mb={4}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                                Cjenik <b>{priceList.fileName}</b> je već objavljen i ostaje u javnoj arhivi.
                                Učitana datoteka bit će objavljena kao nova verzija.
                            </AlertDescription>
                        </Alert>
                    )}

                    <FormControl mb={4} isRequired={!isScheduledEdit} isInvalid={!!fileError}>
                        <FormLabel>Datoteka (.csv ili .xml, max 5 MB)</FormLabel>
                        <Input
                            ref={fileInputRef}
                            type="file"
                            accept={PRICE_LIST_ACCEPT}
                            p={1}
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                        {fileError ? (
                            <FormErrorMessage>{fileError}</FormErrorMessage>
                        ) : isScheduledEdit ? (
                            <FormHelperText>Ostavite prazno da zadržite postojeću datoteku ({priceList.originalFileName}).</FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl mb={4} isRequired>
                        <FormLabel>Vrijedi od</FormLabel>
                        <Input
                            type="date"
                            min={today}
                            value={validForDate}
                            onChange={(e) => setValidForDate(e.target.value)}
                        />
                        <FormHelperText>
                            {validForDate > today
                                ? `Cjenik će javno postati vidljiv ${formatDateOnly(validForDate)}`
                                : "Cjenik će odmah biti javno objavljen."}
                        </FormHelperText>
                    </FormControl>

                    <Box bg="surface.100" borderRadius="lg" p={3} fontSize="sm" color="gray.600">
                        <Text fontWeight="600" mb={1}>Cjenik mora sadržavati:</Text>
                        <UnorderedList spacing={0.5}>
                            <ListItem>naziv, maloprodajnu cijenu, posebni oblik prodaje i sidrenu cijenu</ListItem>
                            <ListItem>za proizvode još: šifru, marku, jedinicu mjere i dostupnost</ListItem>
                        </UnorderedList>
                        <Text mt={2} fontSize="xs" color="gray.500">Sadržaj datoteke se ne provjerava automatski.</Text>
                    </Box>

                    {error && (
                        <Alert status="error" borderRadius="lg" mt={4}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">{error}</AlertDescription>
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        bg="brand.600"
                        color="white"
                        _hover={{ bg: "brand.700" }}
                        mr={3}
                        onClick={handleSave}
                        isLoading={isSaving}
                        loadingText="Spremanje..."
                        isDisabled={isSaving || !canSave}
                    >
                        {isNewVersion ? "Objavi" : isScheduledEdit ? "Spremi" : "Učitaj"}
                    </Button>
                    <Button onClick={onClose} isDisabled={isSaving}>Povratak</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default UploadModal;
